import React, { useState, useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { 
  getAllDetailTreatments, 
  updateDetailTreatment 
} from '../../components/PatientTreatmentDetails/services/detailTreatmentService';

const TreatmentsKanban = () => {
  const [tasks, setTasks] = useState({
    pending: [],
    active: [],
    suspended: [],
    completed: [],
    canceled: []
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsModal, setDetailsModal] = useState({ 
    isOpen: false, 
    treatment: null 
  });
  const [activeMenu, setActiveMenu] = useState(null);

  // Cargar tratamientos al montar el componente
  useEffect(() => {
    loadTreatments();
  }, []);

  const loadTreatments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obtener todos los tratamientos (sin límite de paginación)
      const response = await getAllDetailTreatments({ 
        page: 1, 
        limit: 1000 
      });
      
      if (response.data) {
        // Agrupar tratamientos por estado
        const grouped = {
          pending: [],
          active: [],
          suspended: [],
          completed: [],
          canceled: []
        };
        
        response.data.forEach(treatment => {
          const status = treatment.treatment_info?.status || 'pending';
          if (grouped[status]) {
            grouped[status].push({
              id: treatment._id,
              treatmentData: treatment,
              name: treatment.patient_id?.name || 'Unknown Patient',
              condition: treatment.treatment_info?.name || 'N/A',
              phone: treatment.patient_id?.phone || 'N/A',
              email: treatment.patient_id?.email || 'N/A',
              progress: treatment.treatment_info?.progress_percentage || 0,
              doctor: treatment.doctor_info?.doctor_name || 'Not assigned',
              startDate: treatment.treatment_info?.start_date,
              estimatedEnd: treatment.treatment_info?.estimated_end_date,
              protocolCode: treatment.treatment_info?.protocol_code,
              lastUpdate: treatment.treatment_info?.last_update
            });
          }
        });
        
        setTasks(grouped);
      }
    } catch (err) {
      console.error('Error loading treatments:', err);
      setError(err.message || 'Error loading treatments');
    } finally {
      setLoading(false);
    }
  };

  const showDetails = (treatment) => {
    setDetailsModal({ isOpen: true, treatment });
  };

  const closeModal = () => {
    setDetailsModal({ isOpen: false, treatment: null });
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceColumn = Array.from(tasks[source.droppableId]);
    const destColumn = source.droppableId === destination.droppableId 
      ? sourceColumn 
      : Array.from(tasks[destination.droppableId]);

    const [movedTask] = sourceColumn.splice(source.index, 1);
    destColumn.splice(destination.index, 0, movedTask);

    // Actualizar estado local optimistamente
    const newTasks = {
      ...tasks,
      [source.droppableId]: sourceColumn,
      [destination.droppableId]: destColumn
    };
    setTasks(newTasks);

    // Actualizar en el backend
    try {
      const newStatus = destination.droppableId;
      await updateDetailTreatment(movedTask.id, {
        treatment_info: {
          ...movedTask.treatmentData.treatment_info,
          status: newStatus
        }
      });
      
      console.log(`Treatment ${movedTask.id} status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating treatment status:', err);
      // Revertir cambios en caso de error
      setTasks(tasks);
      alert('Error updating treatment status. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      active: 'primary',
      suspended: 'secondary',
      completed: 'success',
      canceled: 'danger'
    };
    return colors[status] || 'secondary';
  };

  const TaskCard = ({ task, index }) => (
    <Draggable draggableId={task.id} index={index}>
      {(provided) => (
        <div
          className="card mb-2 shadow-sm"
          style={{ fontSize: '0.85rem', cursor: 'grab' }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <div className="card-body p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center flex-grow-1">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                     style={{ width: '2rem', height: '2rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  {task.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-0 fw-semibold">{task.name}</h6>
                  <small className="text-muted">{task.condition}</small>
                </div>
              </div>
            </div>
            
            <div className="mt-2">
              <p className="mb-1 small text-muted">
                <i className="fas fa-code me-1"></i> {task.protocolCode}
              </p>
              <p className="mb-1 small text-muted">
                <i className="fas fa-phone-alt me-1"></i> {task.phone}
              </p>
              <p className="mb-1 small text-muted">
                <i className="fas fa-envelope me-1"></i> {task.email}
              </p>
              <p className="mb-1 small text-muted">
                <i className="fas fa-calendar me-1"></i> Start: {formatDate(task.startDate)}
              </p>
              <p className="mb-1 small text-muted">
                <i className="fas fa-calendar-check me-1"></i> End: {formatDate(task.estimatedEnd)}
              </p>
              <p className="mb-1 small text-muted">
                Doctor: <span className="fw-semibold">{task.doctor}</span>
              </p>
              <div className="mt-2">
                <small className="text-muted">Progress: {task.progress}%</small>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className="progress-bar bg-success" 
                    role="progressbar" 
                    style={{ width: `${task.progress}%` }}
                    aria-valuenow={task.progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  />
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
              <button
                className="btn btn-link btn-sm text-primary p-0 text-decoration-none"
                onClick={() => showDetails(task)}
              >
                View Details
              </button>
              <i
                className="fas fa-ellipsis-h text-muted"
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveMenu(activeMenu === task.id ? null : task.id)}
              />
            </div>

            {activeMenu === task.id && (
              <div 
                className="position-absolute top-0 end-0 bg-white shadow border rounded p-2"
                style={{ zIndex: 1000 }}
              >
                <button className="btn btn-sm btn-light w-100 mb-1">
                  Episodes
                </button>
                <button className="btn btn-sm btn-light w-100 mb-1">
                  Patient Info
                </button>
                <button className="btn btn-sm btn-light w-100 mb-1">
                  Medical Data
                </button>
                <button 
                  className="btn btn-sm btn-primary w-100 mb-1"
                  onClick={() => loadTreatments()}
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );

  const Column = ({ title, tasks, droppableId, status }) => (
    <Droppable droppableId={droppableId}>
      {(provided, snapshot) => (
        <div className="h-100" {...provided.droppableProps} ref={provided.innerRef}>
          <div 
            className={`bg-${getStatusColor(status)} bg-opacity-10 p-2 rounded-top d-flex justify-content-between align-items-center`}
          >
            <h5 className="mb-0 fw-semibold">{title}</h5>
            <span className={`badge bg-${getStatusColor(status)}`}>{tasks.length}</span>
          </div>
          <div 
            className={`bg-white shadow rounded-bottom p-2 ${snapshot.isDraggingOver ? 'bg-light' : ''}`}
            style={{ minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}
          >
            {tasks.map((task, index) => (
              <TaskCard key={task.id} task={task} index={index} />
            ))}
            {provided.placeholder}
            {tasks.length === 0 && (
              <div className="text-center text-muted py-5">
                <i className="fas fa-inbox fa-2x mb-2"></i>
                <p className="mb-0">No treatments</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );

  if (loading) {
    return (
      <div className="container-fluid p-3 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading treatments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-3">
        <div className="alert alert-danger" role="alert">
          <h5 className="alert-heading">Error</h5>
          <p className="mb-0">{error}</p>
          <button className="btn btn-sm btn-danger mt-2" onClick={loadTreatments}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="container-fluid p-3" style={{ backgroundColor: '#f4f4f9' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Treatments Board</h3>
          <button className="btn btn-primary" onClick={loadTreatments}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>
        
        <div className="row g-2">
          <div className="col-12 col-md">
            <Column title="Pending" tasks={tasks.pending} droppableId="pending" status="pending" />
          </div>
          <div className="col-12 col-md">
            <Column title="Active" tasks={tasks.active} droppableId="active" status="active" />
          </div>
          <div className="col-12 col-md">
            <Column title="Suspended" tasks={tasks.suspended} droppableId="suspended" status="suspended" />
          </div>
          <div className="col-12 col-md">
            <Column title="Completed" tasks={tasks.completed} droppableId="completed" status="completed" />
          </div>
          <div className="col-12 col-md">
            <Column title="Canceled" tasks={tasks.canceled} droppableId="canceled" status="canceled" />
          </div>
        </div>

        {detailsModal.isOpen && detailsModal.treatment && (
          <div 
            className="modal d-block" 
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={closeModal}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h5 className="modal-title">Treatment Details</h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <p className="mb-2"><strong>Patient:</strong> {detailsModal.treatment.name}</p>
                      <p className="mb-2"><strong>Condition:</strong> {detailsModal.treatment.condition}</p>
                      <p className="mb-2"><strong>Protocol:</strong> {detailsModal.treatment.protocolCode}</p>
                      <p className="mb-2"><strong>Phone:</strong> {detailsModal.treatment.phone}</p>
                      <p className="mb-2"><strong>Email:</strong> {detailsModal.treatment.email}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-2"><strong>Doctor:</strong> {detailsModal.treatment.doctor}</p>
                      <p className="mb-2"><strong>Start Date:</strong> {formatDate(detailsModal.treatment.startDate)}</p>
                      <p className="mb-2"><strong>Estimated End:</strong> {formatDate(detailsModal.treatment.estimatedEnd)}</p>
                      <p className="mb-2"><strong>Progress:</strong> {detailsModal.treatment.progress}%</p>
                      <p className="mb-2"><strong>Last Update:</strong> {formatDate(detailsModal.treatment.lastUpdate)}</p>
                    </div>
                  </div>
                  
                  {detailsModal.treatment.treatmentData.phases_summary && 
                   detailsModal.treatment.treatmentData.phases_summary.length > 0 && (
                    <div className="mt-3">
                      <h6 className="fw-bold">Phases:</h6>
                      <div className="list-group">
                        {detailsModal.treatment.treatmentData.phases_summary.map((phase, idx) => (
                          <div key={idx} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{phase.phase_name}</span>
                              <span className={`badge bg-${getStatusColor(phase.status)}`}>
                                {phase.status}
                              </span>
                            </div>
                            <small className="text-muted">
                              Steps: {phase.steps_completed}/{phase.steps_total}
                            </small>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default TreatmentsKanban;